// Iter 51 — Small presentational components extracted from Dashboard.jsx.
// These are pure-render helpers — no business logic. Keeping them out of
// the page file lets Dashboard.jsx focus on data orchestration.
import React from "react";
import { Info } from "lucide-react";
import { formatMoney } from "../../lib/constants";

export function KpiCard({ label, value, accent, icon: Icon, testid, sublabel }) {
  return (
    <div
      data-testid={testid}
      className="bt-card bg-[var(--bt-surface)] border border-[var(--bt-border)] hover:border-zinc-600 transition-colors duration-150 rounded-sm p-5"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="bt-label">{label}</p>
          <p className={`font-heading text-3xl mt-2 bt-num ${accent || ""}`}>{value}</p>
          {sublabel && <p className="text-xs text-[var(--bt-text-muted)] mt-1">{sublabel}</p>}
        </div>
        {Icon && (
          <span className="w-9 h-9 rounded-sm border border-[var(--bt-border)] flex items-center justify-center text-[var(--bt-text-muted)]">
            <Icon className="w-4 h-4" />
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Premium metric card for the iter 13 analytics row (Profit Factor, Avg W/L,
 * Max DD, Streak). Renders any custom value via `children`.
 */
export function MetricCard({ testid, label, icon: Icon, tooltip, children }) {
  return (
    <div
      data-testid={testid}
      className="group relative transition-all duration-150 ease-out"
      style={{
        background: "#111111",
        border: "1px solid #1e1e1e",
        borderRadius: "12px",
        padding: "20px",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#00ff88"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1e1e1e"; }}
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-[12px] text-[#6b7280] font-medium tracking-wide flex items-center gap-1.5">
          {label}
          {tooltip && (
            <span
              className="relative inline-flex"
              tabIndex={0}
            >
              <Info className="w-3 h-3 text-[#6b7280] hover:text-white transition-colors cursor-help" />
              <span
                data-testid={`${testid}-tooltip`}
                className="absolute left-1/2 -translate-x-1/2 top-5 z-50 w-56 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 text-[11px] leading-snug text-white bg-[#0a0a0a] border border-[#2a2a2a] rounded-md px-3 py-2 shadow-lg pointer-events-none"
                style={{ whiteSpace: "normal" }}
              >
                {tooltip}
              </span>
            </span>
          )}
        </p>
        {Icon && (
          <Icon className="w-4 h-4 text-[#6b7280] group-hover:text-[#00ff88] transition-colors duration-150" strokeWidth={2} />
        )}
      </div>
      <div className="font-heading bt-num" style={{ fontSize: "28px", fontWeight: 700, lineHeight: 1.1 }}>
        {children}
      </div>
    </div>
  );
}

export function ChartTooltip({ active, payload, currency, theme }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const cumulative = currency === "INR" ? p.cumulative_inr : p.cumulative_usd;
  const daily = currency === "INR" ? p.daily_inr : p.daily_usd;
  const isLight = theme === "light";
  return (
    <div
      style={{
        background: isLight ? "#ffffff" : "var(--bt-bg)",
        border: isLight ? "1px solid rgba(15,23,42,0.08)" : "1px solid var(--bt-border)",
        boxShadow: isLight ? "0 4px 16px rgba(15,23,42,0.10)" : "none",
        color: isLight ? "#0f172a" : "#ffffff",
      }}
      className="px-3 py-2 rounded-sm text-xs font-mono"
    >
      <p className="text-[var(--bt-text-muted)] mb-1">{p.date}</p>
      <p>
        Cumulative: <span className={cumulative >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}>{formatMoney(cumulative, currency)}</span>
      </p>
      <p className="text-[var(--bt-text-muted)]">
        Day: <span className={daily >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}>{formatMoney(daily, currency)}</span>
      </p>
    </div>
  );
}
